import React, { useCallback, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { useNavigate } from 'react-router-dom';
import { CrmBadge, CrmButton, CrmFormDrawer, CrmInput, CrmSelect, CrmTooltip } from '../components/ui';
import { CrmEntityListPage } from '../components/listing';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import { createUser, deleteUser, deleteUserWithReassignment, getAllUsers, getUserDeleteImpact, updateUser } from '../services/userServices';
import { getAllRoles } from '../services/roleServices';
import { formatDate } from '../utils/dateFormatter';

const EMPTY = { id: null, name: '', email: '', phone_number: '', password: '', roleId: '' };
const errorText = (error) => error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Request failed.';

export default function Users() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [userRows, roleRows] = await Promise.all([getAllUsers(), getAllRoles()]);
      setUsers((Array.isArray(userRows) ? userRows : []).filter((row) => String(row.role_name || row.role || '').toLowerCase() !== 'customer'));
      setRoles(Array.isArray(roleRows) ? roleRows : []);
      setError('');
    } catch (loadError) {
      setError(errorText(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const staffRoles = useMemo(() => roles.filter((role) => String(role.name || '').toLowerCase() !== 'customer'), [roles]);
  const roleOptions = useMemo(() => staffRoles.map((role) => ({ value: String(role.id), label: role.name })), [staffRoles]);

  const openCreate = () => setForm({ ...EMPTY, roleId: String(staffRoles[0]?.id || '') });
  const openEdit = (row) => setForm({ ...EMPTY, ...row, password: '', roleId: String(row.role_id || '') });

  const save = async () => {
    if (!form?.name?.trim() || !form?.email?.trim() || !form?.roleId) throw new Error('Name, email and role are required.');
    if (!form.id && String(form.password || '').length < 6) throw new Error('Password must be at least 6 characters.');
    if (form.id) {
      await updateUser(form.id, { name: form.name.trim(), email: form.email.trim(), phone_number: form.phone_number?.trim() || null, roleId: Number(form.roleId) });
    } else {
      await createUser({ name: form.name.trim(), email: form.email.trim(), phone_number: form.phone_number?.trim() || null, password: form.password, roleId: Number(form.roleId) });
    }
    setForm(null);
    setNotification({ open: true, message: form.id ? 'User updated.' : 'Staff user created.', severity: 'success' });
    await load();
  };

  const startDelete = async (row) => {
    if (Number(row.id) === Number(currentUser?.id)) {
      setNotification({ open: true, message: 'You cannot delete your own account.', severity: 'error' });
      return;
    }
    try {
      const payload = await getUserDeleteImpact(row.id);
      if (!payload?.impact?.totalAssignments) {
        const accepted = await confirm({ title: 'Delete user?', message: `Delete ${row.name || row.email}? This cannot be undone.`, confirmText: 'Delete' });
        if (!accepted) return;
        await deleteUser(row.id);
        setNotification({ open: true, message: 'User deleted.', severity: 'success' });
        await load();
        return;
      }
      setDeleteState({ user: row, impact: payload.impact, reassignToUserId: '' });
    } catch (deleteError) {
      setNotification({ open: true, message: errorText(deleteError), severity: 'error' });
    }
  };

  const confirmReassignDelete = async () => {
    if (!deleteState?.reassignToUserId) throw new Error('Choose a staff user to receive the linked records.');
    await deleteUserWithReassignment(deleteState.user.id, Number(deleteState.reassignToUserId));
    setDeleteState(null);
    setNotification({ open: true, message: 'Linked records were reassigned and the user was deleted.', severity: 'success' });
    await load();
  };

  const columns = useMemo(() => [
    { id: 'name', label: 'Name', width: 220, minWidth: 120, hideable: false, defaultPinned: true, pinnable: true, accessor: (row) => row.name || '—' },
    { id: 'email', label: 'Email', width: 260, minWidth: 150, accessor: (row) => row.email || '—' },
    { id: 'phone_number', label: 'Phone', width: 160, minWidth: 100, accessor: (row) => row.phone_number || '—' },
    { id: 'role_name', label: 'Role', width: 150, minWidth: 90, filterable: true, accessor: (row) => row.role_name || row.role || '—', renderCell: (row) => <CrmBadge tone={String(row.role_name || row.role || '').toLowerCase() === 'admin' ? 'info' : 'neutral'}>{row.role_name || row.role || 'Unassigned'}</CrmBadge> },
    { id: 'created_at', label: 'Created', width: 150, minWidth: 110, type: 'date', accessor: (row) => row.created_at, renderCell: (row) => formatDate(row.created_at) || '—' },
    {
      id: 'row_actions', label: 'Actions', width: 110, minWidth: 105, maxWidth: 140, hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (row) => <span className="crm-row-actions">
        <CrmTooltip content="Edit user"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openEdit(row)} aria-label="Edit user"><EditOutlinedIcon fontSize="small" /></CrmButton></CrmTooltip>
        <CrmTooltip content={Number(row.id) === Number(currentUser?.id) ? 'You cannot delete your own account' : 'Delete user'}><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" disabled={Number(row.id) === Number(currentUser?.id)} onClick={() => startDelete(row)} aria-label="Delete user"><DeleteOutlineIcon fontSize="small" /></CrmButton></CrmTooltip>
      </span>,
    },
  ], [currentUser?.id]);

  const filters = useMemo(() => [
    { id: 'role', columnId: 'role_name', label: 'Role', type: 'select', quick: true, options: roleOptions, getValue: (row) => String(row.role_id || '') },
    { id: 'created_at', columnId: 'created_at', label: 'Created', type: 'date-range', quick: true, getValue: (row) => row.created_at },
  ], [roleOptions]);

  const exportRows = (rows) => {
    const data = rows.map((row) => ({ Name: row.name || '', Email: row.email || '', Phone: row.phone_number || '', Role: row.role_name || row.role || '', Created: formatDate(row.created_at) || '' }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Users');
    XLSX.writeFile(workbook, 'users.xlsx');
  };

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="users"
        title="Users"
        itemLabel="user"
        items={users}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'created_at', direction: 'desc' }}
        loading={loading}
        error={error}
        onRetry={load}
        onRefresh={load}
        onCreate={openCreate}
        createLabel="Create user"
        onExport={exportRows}
        onExportSelected={exportRows}
        headerActions={<CrmButton size="small" variant="secondary" startIcon={<SecurityOutlinedIcon />} onClick={() => navigate('/users/access')}>Roles & permissions</CrmButton>}
        summaryItems={[
          { label: 'Staff users', value: users.length },
          { label: 'Admins', value: users.filter((row) => String(row.role_name || row.role || '').toLowerCase() === 'admin').length },
          { label: 'Roles in use', value: new Set(users.map((row) => row.role_id).filter(Boolean)).size },
        ]}
        emptyTitle="No staff users"
        emptyMessage="Create staff accounts here. Storefront customers are managed separately through Customer Accounts."
      />

      <CrmFormDrawer open={Boolean(form)} onClose={() => setForm(null)} onSubmit={save} mode={form?.id ? 'edit' : 'create'} title={form?.id ? 'Edit staff user' : 'Create staff user'} subtitle="Dashboard staff identities are separate from Storefront customer accounts." canSave={Boolean(form?.name?.trim() && form?.email?.trim() && form?.roleId && (form?.id || String(form?.password || '').length >= 6))}>
        {form ? <>
          <CrmInput label="Name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required />
          <CrmInput label="Email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} required />
          <CrmInput label="Phone" value={form.phone_number || ''} onChange={(event) => setForm((current) => ({ ...current, phone_number: event.target.value }))} />
          {!form.id ? <CrmInput label="Temporary password" type="password" autoComplete="new-password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} helperText="Minimum 6 characters." required /> : null}
          <CrmSelect label="Role" value={form.roleId} onChange={(event) => setForm((current) => ({ ...current, roleId: event.target.value }))} required disabled={String(form.role_name || form.role || '').toLowerCase() === 'admin'}>
            <option value="">Select role</option>
            {staffRoles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
          </CrmSelect>
        </> : null}
      </CrmFormDrawer>

      <CrmFormDrawer open={Boolean(deleteState)} onClose={() => setDeleteState(null)} onSubmit={confirmReassignDelete} title="Reassign linked records" subtitle={`${deleteState?.user?.name || 'This user'} owns or is referenced by ${deleteState?.impact?.totalAssignments || 0} record(s). JDH will reassign those references before deletion.`} saveLabel="Reassign & delete" canSave={Boolean(deleteState?.reassignToUserId)}>
        {deleteState ? <>
          <div className="phase13-warning">{(deleteState.impact.assignments || []).slice(0, 8).map((item) => `${item.table}.${item.column}: ${item.count}`).join(' · ')}</div>
          <CrmSelect label="Reassign to" value={deleteState.reassignToUserId} onChange={(event) => setDeleteState((current) => ({ ...current, reassignToUserId: event.target.value }))} required>
            <option value="">Select staff user</option>
            {users.filter((user) => Number(user.id) !== Number(deleteState.user.id)).map((user) => <option key={user.id} value={user.id}>{user.name || user.email} — {user.email}</option>)}
          </CrmSelect>
        </> : null}
      </CrmFormDrawer>

      <NotificationSnackbar {...notification} onClose={() => setNotification((current) => ({ ...current, open: false }))} />
    </div>
  );
}
